import { Link, useLocation } from 'react-router-dom';
import { Search, BookOpen, KeyRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NIP41_DRAFT_VERSION } from '@/lib/nip41';

interface HeirShellProps {
  children: React.ReactNode;
}

const navItems = [
  { path: '/learn', label: 'Learn', icon: BookOpen },
  { path: '/inspect', label: 'Inspect', icon: Search },
];

export function HeirShell({ children }: HeirShellProps) {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-black text-slate-200 flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800 bg-black/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 items-center h-16">
            {/* Logo — left */}
            <Link to="/" className="flex items-center space-x-3 group justify-self-start">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center group-hover:border-cyan-400 transition-colors">
                <KeyRound className="w-4 h-4 text-cyan-400" />
              </div>
              <div className="flex items-baseline space-x-2">
                <span className="text-xl font-bold text-white tracking-tight">Heir</span>
                <span className="text-xs text-slate-500 hidden sm:inline">NIP-41 Companion</span>
              </div>
            </Link>

            {/* Navigation — center */}
            <nav className="hidden md:flex items-center justify-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                      isActive
                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Right — empty to balance the logo */}
            <div className="justify-self-end" />
          </div>
        </div>
      </header>

      {/* Mobile Nav */}
      <div className="md:hidden border-b border-slate-800 bg-slate-950/50">
        <div className="flex overflow-x-auto px-4 py-2 space-x-2 no-scrollbar">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all',
                  isActive
                    ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30'
                    : 'text-slate-400 hover:text-slate-200'
                )}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
          <div className="text-xs text-slate-600">
            Targeting <span className="text-slate-500 font-mono">{NIP41_DRAFT_VERSION}</span> — draft status
          </div>
          <div className="text-xs text-slate-600">
            Vibed with <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-400 transition-colors">Shakespeare</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
